import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { toast } from "sonner";
import { BookOpen, Plus, X, Edit2, Trash2, Clock, Euro, ChevronDown, ChevronUp, RefreshCw, Search, CheckSquare, Layers, Cpu, Wifi, Package, Rocket, Loader2, BrainCircuit, FolderOpen } from "lucide-react";
import AttachCostModal from "@/components/AttachCostModal";

const CAT_COLORS = {
  "Plataforma TIC": "bg-green-100 text-green-700",
  "API / Movilidad": "bg-blue-100 text-blue-700",
  "GIS / Cartografía": "bg-teal-100 text-teal-700",
  "Integración IoT/RFID": "bg-orange-100 text-orange-700",
  "App Ciudadano": "bg-pink-100 text-pink-700",
  "Gestión Flota": "bg-purple-100 text-purple-700",
  "Infraestructura": "bg-gray-100 text-gray-600",
  "Desarrollo a Medida": "bg-amber-100 text-amber-700",
  "RSU / Limpieza": "bg-lime-100 text-lime-700",
  "Movilidad / Tráfico": "bg-sky-100 text-sky-700",
  "Zonas Verdes": "bg-emerald-100 text-emerald-700",
  "BI / Analítica": "bg-violet-100 text-violet-700",
  "RRHH / Empleado": "bg-rose-100 text-rose-700",
  "ERP / Finanzas": "bg-cyan-100 text-cyan-700",
  "Otro": "bg-slate-100 text-slate-600",
};


/* ── Create Project Modal ──────────────────────────────── */
function CreateProjectModal({ service, onClose, onCreated }) {
  const [form, setForm] = useState({
    title: service.nombre,
    description: service.descripcion || "",
    responsible_tech: "",
    responsible_business: "",
    end_date: "",
    municipio: "",
    azure_project: "",
    sync_devops: false,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/catalogo-servicios/${service.id}/create-project`, form);
      toast.success(`Proyecto "${data.project?.title}" creado correctamente`);
      if (data.checklist) toast.success(`Checklist generado con ${data.checklist.total_items} tareas`);
      if (data.devops?.epic_id) toast.success(`Épica #${data.devops.epic_id} creada en Azure DevOps`);
      if (data.devops?.error) toast.warning(`DevOps: ${data.devops.error}`);
      onCreated(data.project);
    } catch (e) {
      toast.error("Error al crear el proyecto");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="create-project-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h3 className="font-bold text-gray-900">Crear Proyecto desde Catálogo</h3>
            <p className="text-xs text-gray-400 mt-0.5">Servicio: <span className="font-medium text-gray-600">{service.nombre}</span></p>
          </div>
          <button onClick={onClose}><X size={18} className="text-gray-400" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Nombre del Proyecto *</label>
            <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
              value={form.title} onChange={e => set("title", e.target.value)} data-testid="project-title-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Descripción</label>
            <textarea rows={2} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934] resize-none"
              value={form.description} onChange={e => set("description", e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Responsable TIC</label>
              <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
                value={form.responsible_tech} onChange={e => set("responsible_tech", e.target.value)} placeholder="David Redondo" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Responsable Negocio</label>
              <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
                value={form.responsible_business} onChange={e => set("responsible_business", e.target.value)} placeholder="Nombre cliente" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Municipio</label>
              <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
                value={form.municipio} onChange={e => set("municipio", e.target.value)} placeholder="ej. Madrid" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Fecha límite estimada</label>
              <input type="date" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
                value={form.end_date} onChange={e => set("end_date", e.target.value)} />
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <input type="checkbox" id="sync_devops" checked={form.sync_devops} onChange={e => set("sync_devops", e.target.checked)}
                  className="w-4 h-4 accent-[#007934]" data-testid="sync-devops-checkbox" />
                <label htmlFor="sync_devops" className="text-sm font-medium text-gray-700">Sincronizar con Azure DevOps</label>
              </div>
              <span className="text-[10px] text-gray-400 bg-white border border-gray-200 px-2 py-0.5 rounded-full">Opcional</span>
            </div>
            {form.sync_devops && (
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Proyecto Azure DevOps</label>
                <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
                  value={form.azure_project} onChange={e => set("azure_project", e.target.value)}
                  placeholder="ej. Valoriza Park" data-testid="azure-project-input" />
                <p className="text-[11px] text-gray-400 mt-1">Se creará una Épica + {service.tareas_template?.length || 0} tareas en Azure DevOps</p>
              </div>
            )}
          </div>

          <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
            <p className="text-xs text-blue-700 font-medium mb-1">Se generará automáticamente:</p>
            <ul className="text-xs text-blue-600 space-y-0.5">
              <li>• Proyecto en la plataforma</li>
              <li>• Checklist con {service.tareas_template?.length || 0} tareas predefinidas</li>
              {form.sync_devops && <li>• Épica + work items en Azure DevOps</li>}
            </ul>
          </div>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm border border-gray-200 text-gray-600 hover:bg-gray-50">Cancelar</button>
          <button onClick={save} disabled={saving || !form.title.trim()} data-testid="confirm-create-project-btn"
            className="px-5 py-2 rounded-lg text-sm bg-[#007934] text-white font-semibold hover:bg-[#006528] disabled:opacity-50 flex items-center gap-2">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Rocket size={13} />}
            {saving ? "Creando…" : "Crear Proyecto"}
          </button>
        </div>
      </div>
    </div>
  );
}


function ServicioCard({ s, onEdit, onDelete, onCreateProject }) {
  const [open, setOpen] = useState(false);
  const [showCost, setShowCost] = useState(false);
  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden" data-testid={`catalogo-card-${s.id}`}>
      <div className="px-5 py-4 cursor-pointer" onClick={() => setOpen(o => !o)}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${CAT_COLORS[s.categoria] || CAT_COLORS["Otro"]}`}>{s.categoria}</span>
            </div>
            <h3 className="font-bold text-gray-900 text-sm">{s.nombre}</h3>
            <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{s.descripcion}</p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-bold text-[#007934]">{s.duracion_min}–{s.duracion_max} <span className="text-xs text-gray-400 font-normal">meses</span></p>
              <p className="text-xs text-gray-500">{s.horas_min.toLocaleString("es-ES")}–{s.horas_max.toLocaleString("es-ES")}h</p>
            </div>
            {open ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
          </div>
        </div>
      </div>

      {open && (
        <div className="border-t border-gray-100 px-5 py-4 space-y-3 bg-gray-50">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white rounded-lg p-3 border border-gray-100">
              <div className="flex items-center gap-1.5 text-[#007934] mb-1"><Clock size={13} /></div>
              <p className="text-base font-bold text-gray-900">{s.duracion_min}–{s.duracion_max}</p>
              <p className="text-[11px] text-gray-400">meses estimados</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-gray-100">
              <div className="flex items-center gap-1.5 text-blue-500 mb-1"><Layers size={13} /></div>
              <p className="text-base font-bold text-gray-900">{s.horas_min.toLocaleString("es-ES")}–{s.horas_max.toLocaleString("es-ES")}</p>
              <p className="text-[11px] text-gray-400">horas estimadas</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-gray-100">
              <div className="flex items-center gap-1.5 text-amber-500 mb-1"><Euro size={13} /></div>
              <p className="text-base font-bold text-gray-900">{s.coste_min ? `${s.coste_min.toLocaleString("es-ES")}–${s.coste_max.toLocaleString("es-ES")}€` : "—"}</p>
              <p className="text-[11px] text-gray-400">coste estimado</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-gray-100">
              <div className="flex items-center gap-1.5 text-purple-500 mb-1"><CheckSquare size={13} /></div>
              <p className="text-base font-bold text-gray-900">{s.tareas_template?.length || 0}</p>
              <p className="text-[11px] text-gray-400">tareas en checklist</p>
            </div>
          </div>

          {s.componentes_software?.length > 0 && (
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase mb-1.5 flex items-center gap-1"><Cpu size={10} /> Software / Plataformas</p>
              <div className="flex flex-wrap gap-1">
                {s.componentes_software.map(c => <span key={c} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">{c}</span>)}
              </div>
            </div>
          )}
          {s.componentes_hardware?.length > 0 && (
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase mb-1.5 flex items-center gap-1"><Package size={10} /> Hardware</p>
              <div className="flex flex-wrap gap-1">
                {s.componentes_hardware.map(c => <span key={c} className="text-xs bg-orange-50 text-orange-700 px-2 py-0.5 rounded">{c}</span>)}
              </div>
            </div>
          )}
          {s.integraciones?.length > 0 && (
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase mb-1.5 flex items-center gap-1"><Wifi size={10} /> Integraciones</p>
              <div className="flex flex-wrap gap-1">
                {s.integraciones.map(c => <span key={c} className="text-xs bg-teal-50 text-teal-700 px-2 py-0.5 rounded">{c}</span>)}
              </div>
            </div>
          )}
          {s.tareas_template?.length > 0 && (
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase mb-1.5 flex items-center gap-1"><CheckSquare size={10} /> Checklist de tareas ({s.tareas_template.length})</p>
              <ol className="space-y-0.5">
                {s.tareas_template.map((t, i) => (
                  <li key={i} className="text-xs text-gray-600 flex items-start gap-1.5">
                    <span className="text-[10px] text-gray-400 font-mono mt-0.5 w-4 flex-shrink-0">{i+1}.</span>{t}
                  </li>
                ))}
              </ol>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2 flex-wrap">
            <button onClick={() => setShowCost(true)} className="flex items-center gap-1 text-xs bg-amber-500 hover:bg-amber-600 text-white px-3 py-1.5 rounded-lg font-semibold" data-testid={`servicio-cost-btn-${s.id}`} title="Añadir como CAPEX/OPEX al presupuesto de una licitación">
              <Euro size={11}/> Llevar a coste
            </button>
            <button onClick={() => onCreateProject(s)} className="flex items-center gap-1 text-xs border border-[#007934]/30 text-[#007934] px-3 py-1.5 rounded-lg hover:bg-[#007934]/5 font-medium" data-testid={`create-project-btn-${s.id}`}><Rocket size={11} /> Crear Proyecto</button>
            <button onClick={() => onEdit(s)} className="flex items-center gap-1 text-xs border border-gray-200 text-gray-500 px-3 py-1.5 rounded-lg hover:bg-gray-100"><Edit2 size={11} /> Editar</button>
            <button onClick={() => onDelete(s.id)} className="flex items-center gap-1 text-xs border border-red-200 text-red-500 px-3 py-1.5 rounded-lg hover:bg-red-50"><Trash2 size={11} /> Eliminar</button>
          </div>
        </div>
      )}
      <AttachCostModal
        open={showCost}
        onClose={() => setShowCost(false)}
        source={{
          type: "servicio",
          id: s.id,
          nombre: s.nombre,
          defaultConcepto: `${s.nombre} — implantación`,
          defaultImporte: Math.round(((s.coste_min || 0) + (s.coste_max || 0)) / 2) || 0,
          defaultTipo: "capex",
          defaultUnidad: "proyecto",
        }}
      />
    </div>
  );
}

function ServicioFormModal({ existing, onClose, onSaved }) {
  const empty = { nombre: "", categoria: "Plataforma TIC", descripcion: "", duracion_min: 1, duracion_max: 3, horas_min: 100, horas_max: 300, coste_min: "", coste_max: "", componentes_software: "", componentes_hardware: "", integraciones: "", tareas_template: "" };
  const toForm = (s) => s ? { ...s, componentes_software: (s.componentes_software||[]).join("\n"), componentes_hardware: (s.componentes_hardware||[]).join("\n"), integraciones: (s.integraciones||[]).join("\n"), tareas_template: (s.tareas_template||[]).join("\n"), coste_min: s.coste_min||"", coste_max: s.coste_max||"" } : empty;
  const [form, setForm] = useState(toForm(existing));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const CATEGORIAS = ["Plataforma TIC","API / Movilidad","GIS / Cartografía","Integración IoT/RFID","App Ciudadano","Gestión Flota","Infraestructura","Desarrollo a Medida","RSU / Limpieza","Movilidad / Tráfico","Zonas Verdes","BI / Analítica","RRHH / Empleado","ERP / Finanzas","Otro"];

  const save = async () => {
    if (!form.nombre.trim()) return toast.error("El nombre es obligatorio");
    setSaving(true);
    const payload = { ...form, componentes_software: form.componentes_software.split("\n").filter(Boolean), componentes_hardware: form.componentes_hardware.split("\n").filter(Boolean), integraciones: form.integraciones.split("\n").filter(Boolean), tareas_template: form.tareas_template.split("\n").filter(Boolean), coste_min: form.coste_min ? parseFloat(form.coste_min) : null, coste_max: form.coste_max ? parseFloat(form.coste_max) : null, duracion_min: parseInt(form.duracion_min), duracion_max: parseInt(form.duracion_max), horas_min: parseInt(form.horas_min), horas_max: parseInt(form.horas_max) };
    try {
      if (existing) await axios.put(`${API}/catalogo-servicios/${existing.id}`, payload);
      else await axios.post(`${API}/catalogo-servicios`, payload);
      toast.success("Guardado");
      onSaved();
    } catch { toast.error("Error"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="font-bold text-gray-900">{existing ? "Editar servicio" : "Nuevo servicio TIC"}</h3>
          <button onClick={onClose}><X size={18} className="text-gray-400" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Nombre *</label>
              <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" value={form.nombre} onChange={e => set("nombre", e.target.value)} />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Categoría</label>
              <select className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" value={form.categoria} onChange={e => set("categoria", e.target.value)}>
                {CATEGORIAS.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Descripción</label>
              <textarea rows={2} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934] resize-none" value={form.descripcion} onChange={e => set("descripcion", e.target.value)} />
            </div>
            <div><label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Duración mín (meses)</label><input type="number" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" value={form.duracion_min} onChange={e => set("duracion_min", e.target.value)} /></div>
            <div><label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Duración máx (meses)</label><input type="number" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" value={form.duracion_max} onChange={e => set("duracion_max", e.target.value)} /></div>
            <div><label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Horas mín</label><input type="number" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" value={form.horas_min} onChange={e => set("horas_min", e.target.value)} /></div>
            <div><label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Horas máx</label><input type="number" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" value={form.horas_max} onChange={e => set("horas_max", e.target.value)} /></div>
            <div><label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Coste mín (€)</label><input type="number" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" value={form.coste_min} onChange={e => set("coste_min", e.target.value)} /></div>
            <div><label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Coste máx (€)</label><input type="number" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" value={form.coste_max} onChange={e => set("coste_max", e.target.value)} /></div>
            <div className="col-span-2"><label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Software / Plataformas (uno por línea)</label><textarea rows={3} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934] resize-none" value={form.componentes_software} onChange={e => set("componentes_software", e.target.value)} /></div>
            <div className="col-span-2"><label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Hardware (uno por línea)</label><textarea rows={2} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934] resize-none" value={form.componentes_hardware} onChange={e => set("componentes_hardware", e.target.value)} /></div>
            <div className="col-span-2"><label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Integraciones (una por línea)</label><textarea rows={2} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934] resize-none" value={form.integraciones} onChange={e => set("integraciones", e.target.value)} /></div>
            <div className="col-span-2"><label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Tareas checklist (una por línea)</label><textarea rows={5} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934] resize-none" value={form.tareas_template} onChange={e => set("tareas_template", e.target.value)} placeholder="Levantamiento de requisitos&#10;Setup infraestructura&#10;..." /></div>
          </div>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm border border-gray-200 text-gray-600 hover:bg-gray-50">Cancelar</button>
          <button onClick={save} disabled={saving} className="px-5 py-2 rounded-lg text-sm bg-[#007934] text-white font-semibold hover:bg-[#006528] disabled:opacity-50 flex items-center gap-2">
            {saving && <RefreshCw size={12} className="animate-spin" />} Guardar
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CatalogoServicios() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("Todos");
  const [seedingVSM, setSeedingVSM] = useState(false);
  const [createProjectFor, setCreateProjectFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/catalogo-servicios`); setList(r.data || []); }
    catch { toast.error("Error cargando catálogo"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("¿Eliminar este servicio del catálogo?")) return;
    await axios.delete(`${API}/catalogo-servicios/${id}`);
    toast.success("Eliminado");
    load();
  };

  const handleSeedVSM = async () => {
    if (!window.confirm("¿Importar los servicios del catálogo VSM Excel? Se añadirán los nuevos (no se duplicarán los existentes).")) return;
    setSeedingVSM(true);
    try {
      const { data } = await axios.post(`${API}/catalogo-servicios/seed-vsm`);
      toast.success(`Importados ${data.added} servicios del catálogo VSM (${data.skipped} ya existían)`);
      load();
    } catch {
      toast.error("Error al importar el catálogo VSM");
    } finally {
      setSeedingVSM(false);
    }
  };

  const cats = ["Todos", ...new Set(list.map(s => s.categoria))];
  const filtered = list.filter(s => {
    const q = search.toLowerCase();
    const matchQ = !q || s.nombre.toLowerCase().includes(q) || s.descripcion?.toLowerCase().includes(q);
    const matchCat = filterCat === "Todos" || s.categoria === filterCat;
    return matchQ && matchCat;
  });

  const totalHoras = list.reduce((s, x) => s + ((x.horas_min + x.horas_max) / 2), 0);

  return (
    <div className="p-6 space-y-6 w-full">
      {(showForm || editing) && (
        <ServicioFormModal existing={editing} onClose={() => { setShowForm(false); setEditing(null); }} onSaved={() => { setShowForm(false); setEditing(null); load(); }} />
      )}
      {createProjectFor && (
        <CreateProjectModal
          service={createProjectFor}
          onClose={() => setCreateProjectFor(null)}
          onCreated={(proj) => { setCreateProjectFor(null); }}
        />
      )}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-['Outfit']">Catálogo de Servicios TIC</h1>
          <p className="text-sm text-gray-500 mt-0.5">{list.length} servicios · estimaciones de esfuerzo y coste escalables</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleSeedVSM} disabled={seedingVSM} data-testid="seed-vsm-btn"
            className="flex items-center gap-2 border border-[#007934]/30 text-[#007934] px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-[#007934]/5 transition-colors disabled:opacity-50">
            {seedingVSM ? <Loader2 size={14} className="animate-spin" /> : <FolderOpen size={14} />}
            Importar Catálogo VSM
          </button>
          <button onClick={() => { setEditing(null); setShowForm(true); }} className="flex items-center gap-2 bg-[#007934] text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-[#006528] shadow-sm">
            <Plus size={16} /> Nuevo servicio
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3"><BookOpen size={18} className="text-[#007934]" /><div><p className="text-xl font-bold text-gray-900">{list.length}</p><p className="text-xs text-gray-400">Servicios en catálogo</p></div></div>
        <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3"><Clock size={18} className="text-blue-500" /><div><p className="text-xl font-bold text-gray-900">{Math.round(totalHoras / 1000 * 10) / 10}K h</p><p className="text-xs text-gray-400">Horas medias totales</p></div></div>
        <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3"><CheckSquare size={18} className="text-purple-500" /><div><p className="text-xl font-bold text-gray-900">{list.reduce((s, x) => s + (x.tareas_template?.length || 0), 0)}</p><p className="text-xs text-gray-400">Tareas en checklists</p></div></div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#007934]"
            placeholder="Buscar servicio..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {cats.map(c => (
            <button key={c} onClick={() => setFilterCat(c)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${filterCat === c ? "bg-[#007934] text-white border-[#007934]" : "border-gray-200 text-gray-500 hover:border-[#007934]"}`}>{c}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><RefreshCw className="animate-spin text-[#007934]" size={24} /></div>
      ) : (
        <div className="space-y-3">
          {filtered.map(s => (
            <ServicioCard key={s.id} s={s} onEdit={sv => { setEditing(sv); setShowForm(true); }} onDelete={del} onCreateProject={setCreateProjectFor} />
          ))}
        </div>
      )}
    </div>
  );
}
